import React, { memo } from "react";

import { WARRANTY_OPTIONS } from "services/api/sales/listings/consts";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { LISTING_INTRO_SAMPLE } from "../consts";
import { getListingTitle } from "../helpers";
import { ListingSceneProps } from "../types";

/** Korak dodatnih informacija: jamstvo, lokacija i kontakt oglasa, prikaz lokacije i zadane postavke. */
const AdditionalInfoScene: React.FC<ListingSceneProps> = ({ formValues }) => {
  const { extra, location, contact } = formValues.marketplaceData;
  const warranty = WARRANTY_OPTIONS.find((option) => option.value === extra.warrantyCode)?.text ?? LISTING_INTRO_SAMPLE.warranty;
  const place = location.manualAddress || formValues.vehicle?.location?.locationName || LISTING_INTRO_SAMPLE.location;
  const hasOwnContact = contact.contactUserIds.length > 0 || !!contact.contactId;
  const isExact = location.display === "exact";

  const rows = [
    { label: "Jamstvo", value: warranty },
    { label: "Lokacija", value: place },
    { label: "Kontakt", value: hasOwnContact ? "Odabran za oglas" : "Iz postavki oglasa" },
  ];

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Dodatne informacije</div>
          <div className="text-sm font-bold text-slate-800 truncate">{getListingTitle(formValues)}</div>
          <div className="mt-3 space-y-2">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
                <span className="text-slate-500 flex-shrink-0">{row.label}</span>
                <span className="font-semibold text-slate-800 truncate">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Prikaz lokacije</div>
          {/* Karta u malom: točna adresa ima iglicu, približna lokacija krug oko područja. */}
          <div className="relative mt-3 h-16 rounded-md bg-gradient-to-br from-emerald-50 to-sky-100 overflow-hidden">
            <span className="absolute left-0 right-0 top-1/2 h-px bg-white" />
            <span className="absolute top-0 bottom-0 left-1/3 w-px bg-white" />
            {isExact ? (
              <span className="absolute left-1/2 top-1/2 w-3 h-3 -ml-1.5 -mt-1.5 rounded-full bg-blue-600 ring-4 ring-blue-200" />
            ) : (
              <span className="absolute left-1/2 top-1/2 w-10 h-10 -ml-5 -mt-5 rounded-full bg-blue-500/20 border border-blue-400" />
            )}
          </div>
          <div className="mt-2 text-sm font-bold text-slate-800 truncate">{isExact ? "Točna adresa" : "Približna lokacija"}</div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Zadano iz postavki"
          subtitle="Kontakt i lokacija su već upisani"
          iconClassName="bg-amber-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 21s7-7.75 7-13a7 7 0 00-14 0c0 5.25 7 13 7 13z" />
              <circle cx="12" cy="8" r="2.5" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(AdditionalInfoScene);
